import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '../hooks/useChat';
import {
    Grid,
    Paper,
    TextField,
    IconButton,
    Typography,
    CircularProgress,
    Box,
    Container,
    Avatar,
    Tooltip
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ReactMarkdown from 'react-markdown'


export const AgentChat = ({
    agentId,
    initialMessages = [],
    className = '',
    placeholder = 'Type a message...',
    onError,
    style = {},
    theme = 'light',
    containerWidth = '100%',
    maxHeight = '600px',
    customStyles = {}
}) => {
    const [input, setInput] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const {
        messages,
        isLoading,
        error,
        currentMessage,
        streamMessage,
        reset
    } = useChat({
        agentId,
        initialMessages,
        onError
    });

    const embedTheme = createTheme({
        palette: {
            mode: theme,
        },
        spacing: 8,
    });

    const defaultStyles = {
        container: {
            width: containerWidth,
            maxWidth: '100%',
            ...customStyles.container
        },
        messageContainer: {
            height: maxHeight,
            overflow: 'auto',
            padding: embedTheme.spacing(2.5),
            backgroundColor: theme === 'light' ? '#f5f5f5' : '#1a1a1a',
            scrollBehavior: 'smooth',
            ...customStyles.messageContainer
        },
        userMessage: {
            backgroundColor: theme === 'light' ? '#e3f2fd' : '#2c387e',
            color: theme === 'light' ? '#000' : '#fff',
            borderRadius: '10px',
            padding: embedTheme.spacing(1.25, 1.875),
            marginBottom: embedTheme.spacing(1.25),
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            ...customStyles.userMessage
        },
        assistantMessage: {
            backgroundColor: theme === 'light' ? '#fff' : '#333',
            color: theme === 'light' ? '#000' : '#fff',
            borderRadius: '10px',
            padding: embedTheme.spacing(1.25, 1.875),
            marginBottom: embedTheme.spacing(1.25),
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            ...customStyles.assistantMessage
        },
        inputContainer: {
            padding: embedTheme.spacing(2.5),
            backgroundColor: theme === 'light' ? '#fff' : '#262626',
            borderTop: `1px solid ${theme === 'light' ? '#e0e0e0' : '#404040'}`,
            ...customStyles.inputContainer
        },
        avatar: {
            backgroundColor: theme === 'light' ? '#1976d2' : '#90caf9',
            color: theme === 'light' ? '#fff' : '#000',
        },
        ...style
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        inputRef.current?.focus();
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const message = input.trim();
        setInput('');
        await streamMessage(message);
        //refocus the input
        inputRef.current?.focus();
    };

    const handleReset = () => {
        reset();
        setInput('');
        inputRef.current?.focus();
    };

    const renderMessage = (message, index) => (
        <Grid
            item
            key={index}
            container
            justifyContent={message.role === 'user' ? 'flex-end' : 'flex-start'}
        >
            <Grid item xs={12} sm={8} md={6}>
                <Box
                    display="flex"
                    alignItems="flex-start"
                    gap={1}
                    style={message.role === 'user' ? defaultStyles.userMessage : defaultStyles.assistantMessage}
                >
                    <Avatar style={defaultStyles.avatar}>
                        {message.role === 'user' ?
                            <PersonIcon /> :
                            <SmartToyIcon />
                        }
                    </Avatar>
                    <Typography style={{ wordBreak: 'break-word', flex: 1 }}>
                        <ReactMarkdown>
                            {message?.content}
                        </ReactMarkdown>
                    </Typography>
                </Box>
            </Grid>
        </Grid>
    );

    const renderMessages = () => {
        const messagesToRender = messages;
        
        return (
            <>
                {messagesToRender.map(renderMessage)}
                
                {isLoading && currentMessage && (
                    <Grid
                        item
                        container
                        justifyContent="flex-start"
                    >
                        <Grid item xs={12} sm={8} md={6}>
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                                style={defaultStyles.assistantMessage}
                            >
                                <Avatar style={defaultStyles.avatar}>
                                    <SmartToyIcon />
                                </Avatar>
                                <Box style={{ flex: 1 }}>
                                    <Typography style={{ wordBreak: 'break-word' }}>
                                        {currentMessage.content}
                                    </Typography>
                                    <Box display="flex" alignItems="center" mt={1}>
                                        <CircularProgress size={16} style={{ marginRight: 8 }} />
                                        <Typography variant="caption" color="textSecondary">
                                            Is Typing...
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                )}
                
                <div ref={messagesEndRef} />
            </>
        );
    };

    return (
        <ThemeProvider theme={embedTheme}>
            <Container style={defaultStyles.container} className={className}>
                <Paper elevation={3}>
                    <Box style={defaultStyles.messageContainer}>
                        <Grid container direction="column" spacing={2}>
                            {renderMessages()}
                        </Grid>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit} style={defaultStyles.inputContainer}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs>
                                <TextField
                                    fullWidth
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={placeholder}
                                    disabled={isLoading}
                                    variant="outlined"
                                    size="small"
                                    inputRef={inputRef}
                                    InputProps={{
                                        style: {
                                            backgroundColor: theme === 'light' ? '#fff' : '#333',
                                            color: theme === 'light' ? '#000' : '#fff',
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <Tooltip title="Reset conversation">
                                    <IconButton
                                        onClick={handleReset}
                                        color="primary"
                                        disabled={isLoading || messages.length === 0}
                                    >
                                        <RestartAltIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                            <Grid item>
                                <Tooltip title="Send message">
                                    <IconButton
                                        type="submit"
                                        disabled={isLoading || !input.trim()}
                                        color="primary"
                                    >
                                        <SendIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Box>

                    {error && (
                        <Box p={2}>
                            <Typography color="error">
                                {error.message}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Container>
        </ThemeProvider>
    );
};